import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, MessageCircle, TrendingUp, Coffee, Star,
  Check, ChevronRight, Calendar, Flame, Award,
} from 'lucide-react';

// ─── Apple Watch Rings-style daily goals ────────────────────────────

interface Ring {
  name: string;
  icon: React.ComponentType<{ className?: string }>;
  current: number;
  goal: number;
  unit: string;
  color: string;
  gradientStart: string;
  gradientEnd: string;
  reward: string;
}

const TODAY_RINGS: Ring[] = [
  { name: 'Greetings', icon: MessageCircle, current: 8, goal: 10, unit: '', color: '#ef4444', gradientStart: '#ef4444', gradientEnd: '#f97316', reward: '+30 pts' },
  { name: 'Upsells', icon: TrendingUp, current: 5, goal: 6, unit: '', color: '#22c55e', gradientStart: '#22c55e', gradientEnd: '#06b6d4', reward: '+60 pts' },
  { name: 'Drinks', icon: Coffee, current: 3, goal: 4, unit: '', color: '#8b5cf6', gradientStart: '#8b5cf6', gradientEnd: '#ec4899', reward: '+20 pts' },
];

interface WeekDay {
  label: string;
  date: string;
  rings: [number, number, number]; // percent filled for each ring
  allClosed: boolean;
}

const WEEK_HISTORY: WeekDay[] = [
  { label: 'Mon', date: 'Mar 10', rings: [100, 83, 75], allClosed: false },
  { label: 'Tue', date: 'Mar 11', rings: [100, 100, 100], allClosed: true },
  { label: 'Wed', date: 'Mar 12', rings: [90, 67, 50], allClosed: false },
  { label: 'Thu', date: 'Mar 13', rings: [100, 100, 75], allClosed: false },
  { label: 'Fri', date: 'Mar 14', rings: [80, 83, 75], allClosed: false }, // today
  { label: 'Sat', date: 'Mar 15', rings: [0, 0, 0], allClosed: false },
  { label: 'Sun', date: 'Mar 16', rings: [0, 0, 0], allClosed: false },
];

const MONTHLY_STREAK = {
  currentStreak: 3,
  longestStreak: 7,
  totalClosed: 14,
  totalDays: 28,
};

const DailyRings: React.FC = () => {
  const navigate = useNavigate();
  const [animatedPcts, setAnimatedPcts] = useState([0, 0, 0]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setAnimatedPcts(TODAY_RINGS.map(r => Math.min((r.current / r.goal) * 100, 100)));
    }, 300);
    return () => clearTimeout(timer);
  }, []);

  const allClosed = TODAY_RINGS.every(r => r.current >= r.goal);

  // SVG ring component
  const RingSVG = ({ size, rings }: { size: number; rings: { pct: number; color: string; gradStart: string; gradEnd: string }[] }) => {
    const center = size / 2;
    const strokeWidth = size * 0.08;
    const gaps = rings.length;
    const totalRadius = (size - strokeWidth * 2) / 2;
    const ringSpacing = totalRadius / (gaps + 0.5);

    return (
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="-rotate-90">
        <defs>
          {rings.map((ring, i) => (
            <linearGradient key={i} id={`ringGrad${i}${size}`} x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor={ring.gradStart} />
              <stop offset="100%" stopColor={ring.gradEnd} />
            </linearGradient>
          ))}
        </defs>
        {rings.map((ring, i) => {
          const r = totalRadius - i * ringSpacing;
          const circumference = 2 * Math.PI * r;
          const dashOffset = circumference * (1 - ring.pct / 100);
          return (
            <g key={i}>
              {/* Background ring */}
              <circle
                cx={center} cy={center} r={r}
                fill="none" stroke="rgb(31,41,55)" strokeWidth={strokeWidth}
                strokeLinecap="round"
              />
              {/* Filled ring */}
              <circle
                cx={center} cy={center} r={r}
                fill="none" stroke={`url(#ringGrad${i}${size})`} strokeWidth={strokeWidth}
                strokeLinecap="round"
                strokeDasharray={circumference}
                strokeDashoffset={dashOffset}
                className="transition-all duration-1000 ease-out"
              />
            </g>
          );
        })}
      </svg>
    );
  };

  return (
    <div className="min-h-screen bg-gray-950">
      {/* Header */}
      <div className="bg-gray-950 border-b border-gray-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <button onClick={() => navigate('/restaurant/mockups')} className="flex items-center gap-1.5 text-gray-500 hover:text-gray-300 text-sm mb-4 transition-colors">
            <ArrowLeft className="w-4 h-4" /> Back to Hub
          </button>
          <h1 className="text-2xl font-bold text-white">Close Your Rings</h1>
          <p className="text-gray-500 text-sm">Friday, March 14</p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
        {/* Main rings display */}
        <div className="bg-gray-900 rounded-2xl border border-gray-800 p-8">
          <div className="flex flex-col sm:flex-row items-center gap-8">
            {/* Big rings */}
            <div className="relative flex-shrink-0">
              <RingSVG
                size={220}
                rings={animatedPcts.map((pct, i) => ({
                  pct,
                  color: TODAY_RINGS[i].color,
                  gradStart: TODAY_RINGS[i].gradientStart,
                  gradEnd: TODAY_RINGS[i].gradientEnd,
                }))}
              />
              <div className="absolute inset-0 flex items-center justify-center">
                {allClosed ? (
                  <div className="text-center">
                    <Star className="w-8 h-8 text-amber-400 mx-auto mb-1 animate-pulse" />
                    <span className="text-xs text-amber-400 font-bold">ALL CLOSED!</span>
                  </div>
                ) : (
                  <div className="text-center">
                    <span className="text-2xl font-bold text-white">{Math.round(animatedPcts.reduce((s, p) => s + p, 0) / 3)}%</span>
                    <br />
                    <span className="text-[10px] text-gray-500 uppercase tracking-wider">overall</span>
                  </div>
                )}
              </div>
            </div>

            {/* Ring details */}
            <div className="flex-1 space-y-4 w-full">
              {TODAY_RINGS.map((ring, i) => {
                const pct = Math.min((ring.current / ring.goal) * 100, 100);
                const closed = ring.current >= ring.goal;
                return (
                  <div key={ring.name} className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: `${ring.color}20` }}>
                      <ring.icon className={`w-5 h-5 ${ring.name === 'Greetings' ? 'text-red-500' : ring.name === 'Upsells' ? 'text-green-500' : 'text-violet-500'}`} />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-medium text-white">{ring.name}</span>
                        <span className="text-sm text-gray-400">
                          <span className="font-bold text-white">{ring.current}</span>/{ring.goal}
                        </span>
                      </div>
                      <div className="h-2 bg-gray-800 rounded-full overflow-hidden">
                        <div
                          className="h-full rounded-full transition-all duration-1000"
                          style={{
                            width: `${pct}%`,
                            background: `linear-gradient(to right, ${ring.gradientStart}, ${ring.gradientEnd})`,
                          }}
                        />
                      </div>
                    </div>
                    <div className="w-16 text-right">
                      {closed ? (
                        <span className="inline-flex items-center gap-0.5 text-xs font-bold text-emerald-400">
                          <Check className="w-3.5 h-3.5" /> Done!
                        </span>
                      ) : (
                        <span className="text-xs text-gray-600">{ring.goal - ring.current} to go</span>
                      )}
                    </div>
                  </div>
                );
              })}

              {!allClosed && (
                <div className="mt-2 p-3 rounded-lg bg-gray-800/50 border border-gray-800">
                  <p className="text-xs text-gray-400">
                    Close all 3 rings today for <span className="text-amber-400 font-bold">+50 bonus points</span> and keep your streak alive!
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Week view */}
        <div className="bg-gray-900 rounded-2xl border border-gray-800 p-6">
          <h2 className="text-sm font-semibold text-white mb-4">This Week</h2>
          <div className="grid grid-cols-7 gap-3">
            {WEEK_HISTORY.map((day, i) => {
              const isToday = day.label === 'Fri';
              const isFuture = i > 4;
              return (
                <div key={day.label} className="text-center">
                  <div className={`text-[10px] mb-1 ${isToday ? 'text-white font-bold' : 'text-gray-600'}`}>
                    {day.label}
                  </div>
                  <div className="relative mx-auto w-12 h-12">
                    {isFuture ? (
                      <div className="w-full h-full rounded-full border border-dashed border-gray-800 flex items-center justify-center">
                        <span className="text-[10px] text-gray-700">—</span>
                      </div>
                    ) : (
                      <>
                        <RingSVG
                          size={48}
                          rings={day.rings.map((pct, j) => ({
                            pct,
                            color: TODAY_RINGS[j].color,
                            gradStart: TODAY_RINGS[j].gradientStart,
                            gradEnd: TODAY_RINGS[j].gradientEnd,
                          }))}
                        />
                        {day.allClosed && (
                          <div className="absolute inset-0 flex items-center justify-center">
                            <Star className="w-3 h-3 text-amber-400" />
                          </div>
                        )}
                      </>
                    )}
                  </div>
                  <div className={`text-[10px] mt-1 ${isToday ? 'text-indigo-400' : 'text-gray-700'}`}>
                    {day.date}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Streak & monthly stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { label: 'Current Streak', value: `${MONTHLY_STREAK.currentStreak} days`, icon: Flame, color: 'text-orange-400' },
            { label: 'Best Streak', value: `${MONTHLY_STREAK.longestStreak} days`, icon: Award, color: 'text-amber-400' },
            { label: 'Days Closed', value: `${MONTHLY_STREAK.totalClosed}/${MONTHLY_STREAK.totalDays}`, icon: Check, color: 'text-emerald-400' },
            { label: 'Close Rate', value: `${Math.round((MONTHLY_STREAK.totalClosed / MONTHLY_STREAK.totalDays) * 100)}%`, icon: Calendar, color: 'text-indigo-400' },
          ].map((stat, i) => (
            <div key={i} className="bg-gray-900 rounded-xl border border-gray-800 p-4 text-center">
              <stat.icon className={`w-5 h-5 ${stat.color} mx-auto mb-1.5`} />
              <div className="text-lg font-bold text-white">{stat.value}</div>
              <div className="text-[10px] text-gray-600 uppercase tracking-wider">{stat.label}</div>
            </div>
          ))}
        </div>

        {/* Motivation */}
        <div className="text-center py-4">
          <p className="text-xs text-gray-600">
            "I got addicted to closing my rings. Now I feel weird if I don't hit all three." — Jamie L., Lazy Bear
          </p>
        </div>
      </div>
    </div>
  );
};

export default DailyRings;
